/**
 * Tutor contract form section.
 * Contract file upload (name kept in sync with the URL) and signature date.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { DatePicker } from '@/components/forms/DatePicker';
import { FileDropzone } from '@/components/common/FileDropzone';
import { TUTOR_CONTRACT_UPLOAD_CONFIG } from '@/components/tutors/tutor-configs';
import { buildContractPatch, defaultUpload } from '@/components/tutors/inperson/tutor-form-utils';

/** Props for {@link TutorContractSection}. */
interface Props {
  formData: Record<string, any>;
  set: (field: string, value: any) => void;
  setBatch?: (patch: Record<string, any>) => void;
  onFileSelect?: (key: string, file: File | null) => void;
}

/** Tutor contract form section. */
export function TutorContractSection({ formData, set, setBatch, onFileSelect }: Readonly<Props>) {
  function handleContractChange(url: string): void {
    const patch = buildContractPatch(url);
    if (setBatch) {
      setBatch(patch);
      return;
    }
    Object.entries(patch).forEach(([k, v]) => set(k, v));
  }

  function handleSignedDateChange(val: string): void {
    set('signedDate', val);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <FileText className="w-4 h-4" />
        Contrat
      </h4>
      <FileDropzone
        label="Document de contrat"
        required
        accept={TUTOR_CONTRACT_UPLOAD_CONFIG.accept}
        maxSize={TUTOR_CONTRACT_UPLOAD_CONFIG.maxSize}
        uploadFn={onFileSelect ? undefined : defaultUpload}
        onFileSelect={onFileSelect ? (file) => onFileSelect('contractFile', file) : undefined}
        value={formData.contractDocumentLink ?? ''}
        onChange={handleContractChange}
      />
      <DatePicker
        label="Date de signature"
        placeholder="JJ/MM/AAAA"
        value={formData.signedDate ?? ''}
        onChange={handleSignedDateChange}
        inputSize="sm"
      />
    </div>
  );
}
